/**
 * SitePick front — `/purchase/complete/` の進行表示（docs/08 §6）
 *
 * - 注文がまだ DB に無いとき: `GET /orders/by-session/{cs}` を数秒ポーリングし、PAID を確認できたら
 *   同じ URL を再読込（サーバが完了表示 + 計測を 1 回だけ出す）。
 * - 注文はあるが案内メール未送信のとき: `notified` になるまで軽くポーリング。
 * - 「案内メールを再送」: `POST /onboarding/resend`（body は `{ session_id }` のみ）。
 *
 * raw onboarding token はこの画面に無い（メールのみ）。
 */

import { announce } from "./live-region.js";

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_MS = 45000;

/**
 * messages ヘルパ。
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * REST のベース URL（末尾スラッシュ付き）。
 *
 * @returns {string}
 */
function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

const RELOAD_KEY_PREFIX = "sp_pc_reload_";

/**
 * この Checkout Session で既に 1 回再読込したか（sessionStorage。無効なら false）。
 * 再読込後もサーバが注文を見つけられない異常時に、再読込を繰り返さないためのガード。
 *
 * @param {string} sessionId
 * @returns {boolean}
 */
function alreadyReloaded(sessionId) {
  try {
    return !!sessionId && window.sessionStorage.getItem(RELOAD_KEY_PREFIX + sessionId) === "1";
  } catch {
    return false;
  }
}

/**
 * @param {string} sessionId
 * @returns {void}
 */
function markReloaded(sessionId) {
  try {
    if (sessionId) {
      window.sessionStorage.setItem(RELOAD_KEY_PREFIX + sessionId, "1");
    }
  } catch {
    /* sessionStorage 不可でも reloadedOnce（ページ内 1 回）は効く */
  }
}

/**
 * 注文確定でページを完了表示に更新する。
 *
 * @param {HTMLElement} root
 * @param {Object} data
 * @returns {void}
 */
function renderReady(root, data) {
  const title = root.querySelector("[data-sp-pending-title]");
  const body = root.querySelector("[data-sp-pending-body]");
  const timeout = root.querySelector("[data-sp-pending-timeout]");
  if (title) {
    title.textContent = msg("purchaseDone", "お申し込みありがとうございます");
  }
  if (body) {
    const num = data && data.order_number ? `（${data.order_number}）` : "";
    body.textContent = msg("purchaseConfirmed", "決済を確認しました。") + num + msg("purchaseMailInfo", " 案内メールをご登録のアドレスにお送りします。");
  }
  if (timeout) {
    timeout.hidden = true;
  }
  announce(msg("purchaseDone", "お申し込みありがとうございます"));
}

/**
 * 「案内メール準備中」→「送信済み」に更新する。
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function renderNotified(root) {
  const notice = root.querySelector("[data-sp-onboarding-notice]");
  if (notice && notice.getAttribute("data-sp-state") !== "sent") {
    notice.setAttribute("data-sp-state", "sent");
    notice.textContent = msg("onboardingSent", "サイト公開に向けたご入力のご案内をメールでお送りしました。メール内のリンクからお進みください。");
  }
}

/**
 * ポーリングループ。
 *
 * @param {HTMLElement} root
 * @param {string} endpoint
 * @param {boolean} haveOrder すでに注文行が表示されている（notified 待ち）
 * @returns {void}
 */
function startPolling(root, endpoint, haveOrder, sessionId) {
  if (!endpoint) {
    return;
  }
  const started = Date.now();
  let reloadedOnce = false;

  const tick = async () => {
    if (Date.now() - started > POLL_MAX_MS) {
      const timeout = root.querySelector("[data-sp-pending-timeout]");
      if (timeout) {
        timeout.hidden = false;
      }
      return;
    }
    try {
      const res = await fetch(endpoint, { credentials: "same-origin", headers: { Accept: "application/json" } });
      if (res.status === 200) {
        const data = await res.json();
        if (!haveOrder) {
          // 初回表示時に注文が無かった（webhook が遅れた）場合は、同じ URL を再読込して
          // サーバに完了画面を描き直させる。purchase / order_created の計測はサーバが
          // 「PAID の実注文 + 未 ack Cookie」で 1 回だけ出すので、JS 側からは送らない
          // （ここで送ると再読込時に二重になる）。replace なので履歴は増えない。
          if (data && data.payment_status === "PAID" && !reloadedOnce && !alreadyReloaded(sessionId)) {
            reloadedOnce = true;
            markReloaded(sessionId);
            window.location.replace(window.location.href);
            return;
          }
          renderReady(root, data);
        }
        if (data && data.notified) {
          renderNotified(root);
          return;
        }
      }
    } catch {
      /* 次の tick で再試行 */
    }
    window.setTimeout(tick, POLL_INTERVAL_MS);
  };

  window.setTimeout(tick, POLL_INTERVAL_MS);
}

/**
 * 「案内メールを再送」ボタン。
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function bindResend(root) {
  const btn = root.querySelector("[data-sp-resend]");
  if (!(btn instanceof HTMLButtonElement)) {
    return;
  }
  const note = root.querySelector("[data-sp-resend-note]");
  const cfg = window.SITEPICK || {};

  btn.addEventListener("click", async () => {
    const sessionId = btn.getAttribute("data-sp-session") || "";
    if (!sessionId) {
      return;
    }
    btn.disabled = true;
    btn.classList.add("is-loading");
    if (note) {
      note.textContent = "";
    }
    try {
      const res = await fetch(`${restBase()}onboarding/resend`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": typeof cfg.nonce === "string" ? cfg.nonce : "",
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.ok && data && data.sent) {
        const text = (data && data.message) || msg("resendSent", "案内メールを再送しました。");
        if (note) {
          note.textContent = text;
        }
        announce(text);
      } else if (res.status === 429) {
        const text = msg("resendRateLimited", "短時間に繰り返し再送はできません。数分おいてお試しください。");
        if (note) {
          note.textContent = text;
        }
        announce(text);
        window.setTimeout(() => {
          btn.disabled = false;
        }, 60000);
        btn.classList.remove("is-loading");
        return;
      } else {
        const text = msg("resendFailed", "再送できませんでした。時間をおいて再度お試しください。");
        if (note) {
          note.textContent = text;
        }
        announce(text);
      }
    } catch {
      if (note) {
        note.textContent = msg("resendFailed", "再送できませんでした。時間をおいて再度お試しください。");
      }
    } finally {
      btn.classList.remove("is-loading");
      // 成功後も 60 秒は連打させない。
      window.setTimeout(() => {
        btn.disabled = false;
      }, 60000);
    }
  });
}

/**
 * 初期化。
 *
 * @returns {void}
 */
export function initPurchaseComplete() {
  const root = document.querySelector("[data-sp-purchase]");
  if (!(root instanceof HTMLElement)) {
    return;
  }

  const endpoint = root.getAttribute("data-sp-purchase-poll") || "";
  const haveOrder = !!root.querySelector("[data-sp-order-number]");
  const notice = root.querySelector("[data-sp-onboarding-notice]");
  const needNotified = !!notice && notice.getAttribute("data-sp-state") !== "sent";

  if (!haveOrder || needNotified) {
    startPolling(root, endpoint, haveOrder, root.getAttribute("data-sp-purchase-session") || "");
  }

  bindResend(root);
}
